import { Injectable, signal, computed } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export type MessageType = 'error' | 'warning' | 'info';
export interface VTIMessage {
  delay: number;
  type: MessageType;
  message: string;
}
export interface VTIConfirm {
  title: string;
  content: string;
  okText: string;
  cancelText: string;
}
@Injectable({
  providedIn: 'root',
})
export class MessageService {
  public create(type: MessageType, message: string, delay: number = 0): void {
    this.vtiMessageSignal.update(() => {
      this.showMessage(delay);

      return { delay, type, message };
    });
  }

  public info(message: string, delay: number): void {
    this.create('info', message, delay);
  }

  public warning(message: string, delay: number): void {
    this.create('warning', message, delay);
  }

  public error(message: string, delay: number): void {
    this.create('error', message, delay);
  }

  public confirm(params: { title: string; content: string; okText: string; cancelText: string }): void {
    this.vtiConfirmSignal.update(() => {
      this.confirmVisibleSubject.next(true);

      return {
        title: params.title,
        content: params.content,
        okText: params.okText,
        cancelText: params.cancelText,
      };
    });
  }

  private showMessage(delay: number): void {
    this.messageVisibleSubject.next(true);

    if (delay) {
      setTimeout(() => {
        this.messageVisibleSubject.next(false);
      }, delay);
    }
  }

  public hideMessage(): void {
    this.messageVisibleSubject.next(false);
  }

  public confirmAfterClose(response: boolean): void {
    this.confirmVisibleSubject.next(false);
    this.confirmAfterCloseSubject.next(response);
  }

  private readonly confirmAfterCloseSubject = new Subject<boolean>();
  private readonly vtiMessageSignal = signal<VTIMessage>({ delay: 0, type: 'info', message: '' });
  private readonly vtiConfirmSignal = signal<VTIConfirm>({ title: '', content: '', okText: '', cancelText: '' });
  private readonly confirmVisibleSubject = new BehaviorSubject<boolean>(false);
  private readonly messageVisibleSubject = new BehaviorSubject<boolean>(false);

  public confirmAfterClose$ = this.confirmAfterCloseSubject.asObservable();
  public vtiMessage = computed(() => this.vtiMessageSignal());
  public vtiConfirm = computed(() => this.vtiConfirmSignal());
  public confirmVisible$ = this.confirmVisibleSubject.asObservable();
  public messageVisible$ = this.messageVisibleSubject.asObservable();
}
